/*!
 * JAWLINE RUN – FIGHTER (90s fighting-game style version of the retro side-scroller)
 *
 * Same gameplay as jawline-run-retro.js; new art: a detailed runner with four fighters
 * (Rookie, Chad Lite, Chad, Gigachad), night stages, flavor pouches after the real packaging.
 *
 * Embedding:
 *   <link rel="stylesheet" href="jawline-run.css">
 *   <link rel="stylesheet" href="jawline-run-retro.css">
 *   <link rel="stylesheet" href="jawline-run-fighter.css">
 *   <div data-jawline-run-fighter data-codes="CODE5,CODE10,CODE15,CODE20"></div>
 *   <script src="jawline-run-fighter-art.js" defer></script>
 *   <script src="jawline-run-fighter.js" defer></script>
 *
 * Same data attributes as the retro version. Events carry detail.variant = 'fighter'.
 */
(function () {
  'use strict';
  if (window.JawlineRunFighter) return;

  // ---------------------------------------------------------------------------
  // World constants (all in buffer pixels; the buffer is ~224 px wide and scaled up)
  // ---------------------------------------------------------------------------
  const T = 16;                 // tile size
  const PW = 12, PH = 22;       // player hitbox
  const PLAYER_SX = 52;         // player x on screen
  const GROUND_H = 56;          // ground thickness on screen
  const RES = 4;                // art density: 4 buffer pixels per world pixel, so sprites carry the detail of a fighting game
  const VIEW_H = 190;           // world pixels visible top to bottom: wide enough to see what comes
  const FLOOR_H = 26;           // wooden floor below the running line, drawn in perspective
  const FLOOR_BACK = 8;         // the floor starts this far behind the running line, so the runner stands on it
  // Jump: tap = short hop (~2 tiles), hold = full jump (~3 tiles, 0.6 s in the air).
  // A second tap in the air triggers one double jump (with a spin) until the next landing.
  const PHY = { G: 1150, JUMP: 345, DJUMP: 310, CUT: 130, MIN_HOLD: 0.07, COYOTE: 0.08, BUFFER: 0.13, SPIN: 0.32 };

  // Balancing: 4 stages, about 70 seconds in total.
  // gap = pit width in tiles, runway = flat tiles after each obstacle, w = segment weights.
  const STAGES = [
    { name: 'Warm-up',    dur: 15, speed: 76,  gap: [2, 2], runway: [4, 6], blockH: 1, gum: 0.8,
      w: { flat: 2, gap: 3, block: 3, walker: 2, hopper: 0, plat: 1 } },
    { name: 'Hard',       dur: 17, speed: 92,  gap: [2, 3], runway: [3, 5], blockH: 2, gum: 0.7,
      w: { flat: 1, gap: 3, block: 3, walker: 3, hopper: 1, plat: 1 } },
    { name: 'Ultra Hard', dur: 18, speed: 108, gap: [2, 4], runway: [3, 4], blockH: 2, gum: 0.65,
      w: { flat: 1, gap: 4, block: 3, walker: 3, hopper: 2, plat: 1 } },
    { name: 'Finish',     dur: 18, speed: 122, gap: [3, 4], runway: [3, 4], blockH: 2, gum: 0.75,
      w: { flat: 0, gap: 4, block: 3, walker: 3, hopper: 3, plat: 1 } }
  ];
  // A run through New York (see jawline-run-fighter-scenery.js): Manhattan morning → Midtown golden hour
  // → Central Park sunset → Central Park by night. Sky colors, sun/moon and the floor live here.
  // floor = ramp light→dark; slab 1 = sidewalk slabs, 0 = park path.
  const THEMES = [
    { sky0: '#2f5f9e', sky1: '#5d8fc8', sky2: '#9cc2e4', sky3: '#f0e2c8', sun: 0, sunCol: '#fff4d0', night: 0, moon: 0, glow: 0, clouds: 1,
      cloud: '#d8e4f0', cloudHi: '#ffffff', cloudDk: '#9ab0c8', slab: 1, floor: ['#e6e1d6', '#d3cdc1', '#c7c1b4', '#b5aea1', '#948e82', '#5f5a52'] },
    { sky0: '#2a2c66', sky1: '#7a4a88', sky2: '#e98a5a', sky3: '#ffd08a', sun: 1, sunCol: '#fff0b0', night: 0, moon: 0, glow: 0, clouds: 0.8,
      cloud: '#c86a5a', cloudHi: '#ffc080', cloudDk: '#7a3a5a', slab: 1, floor: ['#f2d4ac', '#dfbf98', '#d3b28a', '#c09e78', '#9a7a5c', '#604c3a'] },
    { sky0: '#2c2460', sky1: '#8a3e78', sky2: '#f0745a', sky3: '#ffb878', sun: 0.7, sunCol: '#ffe0a0', night: 0, moon: 0, glow: 0, clouds: 0.9,
      cloud: '#b24a6a', cloudHi: '#ff9a7a', cloudDk: '#5a2a5a', slab: 0, floor: ['#c4ada0', '#ae978b', '#a28b80', '#8f7a70', '#6e5c55', '#433634'] },
    { sky0: '#04061a', sky1: '#0c1034', sky2: '#1a1f50', sky3: '#2c2c62', sun: 0, sunCol: '#ffffff', night: 1, moon: 1, glow: 0, clouds: 0.4,
      cloud: '#262a58', cloudHi: '#43478a', cloudDk: '#141634', slab: 0, floor: ['#6a6c86', '#585a74', '#4f516a', '#44465e', '#34364a', '#1e1f2c'] }
  ];
  // The four fighters from the JAWLINER reference art. need = stages cleared to unlock.
  const OUTFITS = [
    { name: 'Rookie',    tag: 'Hard gum',     need: 0, row: 0 },
    { name: 'Chad Lite', tag: 'Nose tape',    need: 2, row: 1 },
    { name: 'Chad',      tag: 'Mouth tape',   need: 3, row: 2 },
    { name: 'Gigachad',  tag: 'Caffeine gum', need: 4, row: 3 }   // his flame aura is part of the video frames
  ];
  const GOLD = '#f4c542';
  const LS_KEY = 'jawlineRunFighter:v1';

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const rand = (a, b) => a + Math.random() * (b - a);
  const randInt = (a, b) => a + ((Math.random() * (b - a + 1)) | 0);
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  const hash = (n) => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
  function weighted(w) {
    let sum = 0;
    for (const k in w) sum += w[k];
    let r = Math.random() * sum;
    for (const k in w) { r -= w[k]; if (r < 0) return k; }
    return 'flat';
  }
  function hexRgb(h) {
    h = h.replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const toHex = (rgb) => '#' + rgb.map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('');
  const mixHex = (a, b, t) => { const A = hexRgb(a), B = hexRgb(b); return toHex(A.map((v, i) => v + (B[i] - v) * t)); };
  const shade = (hex, f) => toHex(hexRgb(hex).map((v) => v * f));
  function inkFor(hex) {
    const [r, g, b] = hexRgb(hex);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#0b0b0d' : '#ffffff';
  }
  // High score names: uppercase letters, digits and spaces, 2–10 characters, no obvious insults
  const BLOCKED = ['FUCK', 'SHIT', 'CUNT', 'DICK', 'COCK', 'PENIS', 'PORN', 'NAZI', 'HITLER', 'NIGG', 'BITCH', 'SLUT', 'WHORE', 'FICK', 'FOTZE', 'HURE', 'ARSCH', 'WICHS', 'SCHWANZ', 'KKK'];
  function cleanName(v) {
    const n = String(v || '').toUpperCase().replace(/\s+/g, ' ').trim();
    if (!/^[A-Z0-9 ]{2,10}$/.test(n)) return '';
    const flat = n.replace(/ /g, '');
    const mapped = flat.replace(/[0134578]/g, (digit) => ({ 0: 'O', 1: 'I', 3: 'E', 4: 'A', 5: 'S', 7: 'T', 8: 'B' })[digit]);
    const variants = [flat, mapped, mapped.replace(/(.)\1+/g, '$1')];
    return /(?:WWW|HTTPS?|DOT(?:COM|NET|ORG|IO|DE))/.test(flat) ||
      BLOCKED.some((w) => variants.some((name) => name.includes(w))) ? '' : n;
  }

  function loadSave() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch (e) { return {}; }
  }
  function writeSave(s) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }

  // ---------------------------------------------------------------------------
  // 3×5 pixel font for in-world text (banners, billboards, +1)
  // ---------------------------------------------------------------------------
  const FONT = {
    A: '.#.#.#####.##.#', B: '##.#.###.#.###.', C: '.###..#..#...##', D: '##.#.##.##.###.', E: '####..##.#..###',
    F: '####..##.#..#..', G: '.###..#.##.#.##', H: '#.##.#####.##.#', I: '###.#..#..#.###', J: '..#..#..##.#.#.',
    K: '#.##.###.#.##.#', L: '#..#..#..#..###', M: '#.########.##.#', N: '##.#.##.##.##.#', O: '####.##.##.####',
    P: '##.#.###.#..#..', Q: '####.##.####..#', R: '##.#.###.#.##.#', S: '.###...#...###.', T: '###.#..#..#..#.',
    U: '#.##.##.##.####', V: '#.##.##.##.#.#.', W: '#.##.########.#', X: '#.##.#.#.#.##.#', Y: '#.##.#.#..#..#.',
    Z: '###..#.#.#..###', 0: '####.##.##.####', 1: '.#.##..#..#.###', 2: '##...#.#.#..###', 3: '##...#.#...###.',
    4: '#.##.####..#..#', 5: '####..##...###.', 6: '.###..####.####', 7: '###..#.#..#..#.', 8: '####.#####.####',
    9: '####.####..###.', '%': '#.#..#.#.#..#.#', '+': '....#.###.#....', '!': '.#..#..#.....#.', '.': '.............#.',
    '-': '......###......', ' ': '...............', "'": '.#..#..........'
  };
  const textW = (s, sc) => (s.length * 4 - 1) * (sc || 1);
  function drawText(c, s, x, y, color, sc, shadow) {
    sc = sc || 1;
    s = String(s).toUpperCase();
    const pass = (dx, dy, col) => {
      c.fillStyle = col;
      for (let i = 0; i < s.length; i++) {
        const g = FONT[s[i]] || FONT[' '];
        for (let p = 0; p < 15; p++) if (g[p] === '#') c.fillRect(x + dx + (i * 4 + (p % 3)) * sc, y + dy + ((p / 3) | 0) * sc, sc, sc);
      }
    };
    if (shadow) pass(sc, sc, shadow);
    pass(0, 0, color);
  }

  // ---------------------------------------------------------------------------
  // Pixel sprites from text maps
  // ---------------------------------------------------------------------------
  function sprite(rows, pal, w) {
    w = w || rows[0].length;
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = rows.length;
    const c = cv.getContext('2d');
    rows.forEach((r, y) => {
      for (let x = 0; x < w; x++) {
        const col = pal[r[x]];
        if (col) { c.fillStyle = col; c.fillRect(x, y, 1, 1); }
      }
    });
    return cv;
  }

  // Runner, side view facing right: big angular jaw, 16×24
  const HEAD = [
    '....KKKKKK......',
    '...KHHHHHHKK....',
    '..KHHHHHHHHHK...',
    '..KHHHHHHHHHHK..',
    '..KHHHHSSSSSSK..',
    '..KHHHSSSSWKSK..',
    '..KHHsSSSSWKSK..',
    '..KHsSsSSSSSSSK.',
    '...KSSSSSSSSSSK.',
    '...KsSSSSSSKKK..',
    '...KsSSSSSSSSK..',
    '....KssSSSSSSK..',
    '....KKssssssKK..',
    '.....KKsssKK....'
  ];
  const BODIES = {
    run0: [
      '....KTTTTTK.....',
      '...KTTTTTTTK....',
      '...KTTTTTSSSK...',
      '...KTTTTTKSSK...',
      '...KtTTTTTKK....',
      '...KPPPPPPK.....',
      '..KPPK..KPPK....',
      '.KSSK....KSSK...',
      'KFFFK.....KFFK..',
      'KKKK......KKKK..'
    ],
    run1: [
      '....KTTTTTK.....',
      '...KTTTTTTTK....',
      '...KTTSSTTTK....',
      '...KTTSSTTTK....',
      '...KtTKKTTTK....',
      '...KPPPPPPK.....',
      '....KPPPPK......',
      '....KSSSK.......',
      '....KFFFFK......',
      '....KKKKKK......'
    ],
    run2: [
      '....KTTTTTK.....',
      '..KSKTTTTTTK....',
      '.KSSKTTTTTTK....',
      '.KSKKTTTTTTK....',
      '..K.KtTTTTTK....',
      '....KPPPPPPK....',
      '...KPPK.KPPK....',
      '...KSSK..KSSK...',
      '..KFFFK...KFFFK.',
      '..KKKK.....KKKK.'
    ],
    jump: [
      '....KTTTTTK.KK..',
      '...KTTTTTTKSSK..',
      '...KTTTTTTTKK...',
      '...KTTTTTTTK....',
      '...KtTTTTTTK....',
      '...KPPPPPPPK....',
      '...KPPK.KPPSK...',
      '..KSSK..KSSFK...',
      '..KFFFK..KFFK...',
      '..KKKK...KKK....'
    ]
  };

  // Runner frames come from the generated sprite sheet: one row per fighter, run frames + jump + flying kick
  const ART = window.JawlineRunFighterArt || {};
  const SHEET = new Image();
  let sheetReady = false;
  const frameCache = {};
  if (ART.runner) { SHEET.onload = () => { sheetReady = true; }; SHEET.src = ART.runner.src; }
  function runnerFrames(outfit) {
    const row = outfit && outfit.row != null ? outfit.row : 0;
    if (frameCache[row]) return frameCache[row];
    const R = ART.runner;
    if (!R || !sheetReady) return null;
    const cutFrame = (col) => {
      const cv = document.createElement('canvas');
      cv.width = R.w; cv.height = R.h;
      cv.getContext('2d').drawImage(SHEET, col * R.w, row * R.h, R.w, R.h, 0, 0, R.w, R.h);
      return cv;
    };
    const n = Array.isArray(R.runFrames) ? R.runFrames[row] : R.runFrames || 6;
    const run = Array.from({ length: n }, (_, i) => cutFrame(i));
    const punch = R.punchCol != null ? cutFrame(R.punchCol) : null;
    return (frameCache[row] = { run, jump: cutFrame(R.jumpCol != null ? R.jumpCol : n), punch, run0: run[0] });
  }

  // Kettle-Bot: a walking kettlebell, 16×14, two step frames
  const BOT_TOP = [
    '.....KKKKKK.....',
    '....KMMMMMMK....',
    '...KMK....KMK...',
    '...KMK....KMK...',
    '..KKKKKKKKKKKK..',
    '.KBKKBBBBBBKKBK.',
    '.KBWWKBBBBKWWBK.',
    '.KBWKKBBBBKKWBK.',
    '.KBBBBBBBBBBBBK.',
    '.KBBBAAAAAABBBK.',
    '..KBBBBBBBBBBK..',
    '...KKKKKKKKKK...'
  ];
  const BOT_FEET = [
    ['...KFK....KFK...', '..KKK......KKK..'],
    ['....KFK..KFK....', '....KKK..KKK....']
  ];
  // Medicine ball hopper, 14×14
  const BALL = [
    '....KKKKKK....',
    '..KKRRRRRRKK..',
    '.KRRRRRRRRRRK.',
    '.KRrRRRRRRRRK.',
    'KRRrRRRRRRRRRK',
    'KYYYYYYYYYYYYK',
    'KRRRWKRRWKRRRK',
    'KRRRWKRRWKRRRK',
    'KYYYYYYYYYYYYK',
    'KRRRRRRRRRRRRK',
    '.KRRRRRRRRRRK.',
    '.KRRRRRRRRRRK.',
    '..KKRRRRRRKK..',
    '....KKKKKK....'
  ];
  // JAWLINER pouch, 12×16: silver foil, flavor blobs in the corners, J logo, headline, statue with bubble
  const POUCH = [
    '.KKKKKKKKKK.',
    'KAAAHHHHHBBK',
    'KAAZZZZZZZBK',
    'KAHHHHHHHHBK',
    'KHSSSJJJSSSK',
    'KSSSSSJSSSSK',
    'KSSSSJJSSSSK',
    'KSTTTTTSSSSK',
    'KSTTTTTTSSSK',
    'KSTTTTSSsssK',
    'KSSSSSSsAAsK',
    'KSSSSSsAAWAK',
    'KBSSSSsAAAAK',
    'KAASSSssAAsK',
    'KAAAASSsssSK',
    '.KKKKKKKKKK.'
  ];
  // The four flavors from the real packaging: main color (blobs, bubble) and second color
  const FLAVORS = [
    { name: 'Blueberry',  a: '#8a7fd6', b: '#5a4fa8' },
    { name: 'Mango',      a: '#ffae1a', b: '#e8452e' },
    { name: 'Mint',       a: '#2fd3b0', b: '#1f9fd6' },
    { name: 'Watermelon', a: '#e8475f', b: '#6cc24a' }
  ];
  const CLOUD = [
    '........WWWW............',
    '......WWWWWWWW..........',
    '...WWWWWWWWWWWWW..WWW...',
    '..WWWWWWWWWWWWWWWWWWWW..',
    '.WWWWWWWWWWWWWWWWWWWWWW.',
    'WWWWWWWWWWWWWWWWWWWWWWWW',
    'WWwwWWWWWWwwWWWWWWWwwWWW',
    '.wwwwwwwwwwwwwwwwwwwwww.'
  ];

  function img(src) { const i = new Image(); if (src) i.src = src; return i; }
  function buildArt() {
    const pouch = (ART.pouches || []).map(img);
    return { pouch, gum: pouch[0], bot: [img(ART.bot0), img(ART.bot1)], ball: img(ART.ball), box: img(ART.crate), beam: img(ART.beam) };
  }

  // ---------------------------------------------------------------------------
  // Level: generated fresh for every run from segments. Pits, block heights and
  // runways are sized from the jump physics, so every run can be finished.
  // ---------------------------------------------------------------------------
  function buildLevel() {
    const ground = [], blocks = [], plats = [], enemies = [], gums = [], starts = [], bounds = [];
    let x = 0, spanStart = -12 * T;
    const gum = (gx, gy) => gums.push({ x: gx, y: gy, f: (Math.random() * FLAVORS.length) | 0 });
    STAGES.forEach((S, i) => {
      starts.push(x);
      const end = x + S.speed * S.dur;
      // Safe runway after the start or a checkpoint, with a reward line
      const lead = i === 0 ? 12 : 6;
      for (let k = 2; k < lead - 1; k++) gum(x + k * T + 8, 10);
      x += lead * T;
      while (x < end - 8 * T) {
        const kind = weighted(S.w);
        if (kind === 'flat') {
          const n = randInt(3, 6);
          if (Math.random() < S.gum) for (let k = 0; k < n; k++) gum(x + k * T + 8, 10);
          x += n * T;
        } else if (kind === 'gap') {
          const n = randInt(S.gap[0], S.gap[1]);
          ground.push([spanStart, x]);
          if (Math.random() < S.gum) for (let k = 1; k <= 3; k++) gum(x + (n * T * k) / 4, 26 + Math.sin((Math.PI * k) / 4) * 14);
          else if (Math.random() < 0.5) for (let k = 0; k < 3; k++) gum(x + (n * T) / 2 + (k - 1) * 10, 78); // double-jump bonus
          x += n * T;
          spanStart = x;
        } else if (kind === 'block') {
          const h = randInt(1, S.blockH), w = randInt(1, 2);
          blocks.push({ x, w: w * T, h: h * T });
          if (Math.random() < S.gum) gum(x + (w * T) / 2, h * T + 12);
          x += w * T;
        } else if (kind === 'walker') {
          const n = randInt(5, 8);
          enemies.push({ type: 'walker', x: x + (n - 2) * T, y: 0, x0: x + T, x1: x + (n - 1) * T - 14, dir: -1 });
          x += n * T;
        } else if (kind === 'hopper') {
          enemies.push({ type: 'hopper', x: x + 2 * T - 7, y: 0, phase: Math.random() * 6 });
          x += 4 * T;
        } else if (kind === 'plat') {
          const n = randInt(4, 6);
          plats.push({ x: x + T, w: (n - 2) * T, y: 40 });
          for (let k = 0; k < n - 2; k++) gum(x + T + k * T + 8, 54);
          x += n * T;
        }
        x += randInt(S.runway[0], S.runway[1]) * T;
      }
      x = Math.ceil(Math.max(x + 3 * T, end) / T) * T;
      bounds.push(x);
    });
    ground.push([spanStart, x + 60 * T]);
    blocks.sort((a, b) => a.x - b.x);
    gums.sort((a, b) => a.x - b.x);
    return { ground, blocks, plats, enemies, gums, starts, bounds };
  }
  const emptyLevel = () => ({ ground: [[-1e9, 1e9]], blocks: [], plats: [], enemies: [], gums: [], starts: [0, 0, 0, 0], bounds: [1, 1, 1, 1] });

  // ---------------------------------------------------------------------------
  // Sound (WebAudio square waves, no files)
  // ---------------------------------------------------------------------------
  const MUSIC_VOL = 0.05;   // background music stays quiet under the effects
  class Sound {
    constructor() { this.ctx = null; this.muted = false; this.musicUrl = ''; this.musicState = 'none'; this.musicBuf = null; this.music = null; this.wantMusic = false; }
    init() {
      if (this.ctx) { if (this.ctx.state === 'suspended') this.ctx.resume(); this.loadMusic(); return; }
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) try { this.ctx = new AC(); } catch (e) { this.ctx = null; }
      this.loadMusic();
    }
    setMuted(m) {
      this.muted = m;
      if (!this.music || !this.ctx) return;
      const g = this.music.gain.gain, t = this.ctx.currentTime;
      g.cancelScheduledValues(t); g.setValueAtTime(g.value, t); g.linearRampToValueAtTime(m ? 0 : MUSIC_VOL, t + 0.08);
    }
    // Background music (data-music): loaded on the first tap, decoded once, looped without a gap
    loadMusic() {
      if (!this.ctx || !this.musicUrl || this.musicState !== 'none') return;
      this.musicState = 'loading';
      fetch(this.musicUrl)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(r.status)))
        .then((data) => new Promise((ok, no) => this.ctx.decodeAudioData(data, ok, no)))
        .then((buf) => {
          // skip the encoder's silence at both ends so the loop has no gap
          const ch = buf.getChannelData(0);
          let a = 0, b = ch.length - 1;
          while (a < b && Math.abs(ch[a]) < 0.003) a++;
          while (b > a && Math.abs(ch[b]) < 0.003) b--;
          this.musicBuf = buf; this.loop = [a / buf.sampleRate, (b + 1) / buf.sampleRate];
          this.musicState = 'ready';
          if (this.wantMusic) this.playMusic(true);
        })
        .catch(() => { this.musicState = 'failed'; });
    }
    playMusic(on) {
      this.wantMusic = on;
      if (!this.ctx) return;
      const t = this.ctx.currentTime;
      if (!on) {
        if (!this.music) return;
        const m = this.music; this.music = null;
        m.gain.gain.cancelScheduledValues(t); m.gain.gain.setValueAtTime(m.gain.gain.value, t); m.gain.gain.linearRampToValueAtTime(0, t + 0.25);
        try { m.src.stop(t + 0.3); } catch (e) { /* already stopped */ }
        return;
      }
      if (this.music || !this.musicBuf) return;
      const gain = this.ctx.createGain(), src = this.ctx.createBufferSource();
      gain.gain.setValueAtTime(0, t); gain.gain.linearRampToValueAtTime(this.muted ? 0 : MUSIC_VOL, t + 0.4);
      src.buffer = this.musicBuf; src.loop = true; src.loopStart = this.loop[0]; src.loopEnd = this.loop[1];
      src.connect(gain); gain.connect(this.ctx.destination);
      src.start(t, this.loop[0]);
      this.music = { src, gain };
    }
    tone(f, dur, type, vol, slide, delay) {
      if (this.muted || !this.ctx) return;
      const c = this.ctx, t = c.currentTime + (delay || 0);
      const o = c.createOscillator(), g = c.createGain();
      o.type = type || 'square';
      o.frequency.setValueAtTime(f, t);
      if (slide) o.frequency.exponentialRampToValueAtTime(slide, t + dur);
      g.gain.setValueAtTime(vol || 0.05, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g); g.connect(c.destination);
      o.start(t); o.stop(t + dur + 0.02);
    }
    jump() { this.tone(240, 0.18, 'square', 0.04, 620); }
    double() { this.tone(520, 0.16, 'square', 0.04, 1240); }
    gum() { this.tone(988, 0.06, 'square', 0.035); this.tone(1319, 0.12, 'square', 0.035, null, 0.06); }
    chomp() { this.tone(170, 0.05, 'square', 0.05, 120); this.tone(150, 0.05, 'square', 0.045, 100, 0.08); this.tone(1319, 0.08, 'square', 0.025, null, 0.02); }
    pop() { this.tone(1800, 0.05, 'square', 0.035, 600); }
    stomp() { this.tone(420, 0.12, 'square', 0.05, 120); }
    stage() { [523, 659, 784, 1046, 784, 1046].forEach((f, i) => this.tone(f, 0.12, 'square', 0.04, null, i * 0.08)); }
    crash() { [494, 466, 440, 415].forEach((f, i) => this.tone(f, 0.16, 'square', 0.05, null, i * 0.14)); }
    // Game over: a bonk, then a descending "wah wah wah, waaah" with a bass line underneath
    gameOver(delay) {
      const d = delay || 0;
      this.tone(330, 0.12, 'square', 0.05, 110, d);
      [392, 370, 349].forEach((f, i) => this.tone(f, 0.26, 'square', 0.045, f * 0.94, d + 0.4 + i * 0.3));
      this.tone(330, 0.95, 'square', 0.045, 247, d + 1.3);
      [196, 185, 175].forEach((f, i) => this.tone(f, 0.26, 'triangle', 0.07, null, d + 0.4 + i * 0.3));
      this.tone(165, 0.95, 'triangle', 0.08, 123, d + 1.3);
    }
    // Victory: rising arpeggio, a held high note over a bass line, then a sparkle
    win() {
      [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.11, 'square', 0.045, null, i * 0.09));
      this.tone(784, 0.14, 'square', 0.045, null, 0.45);
      this.tone(1047, 0.7, 'square', 0.05, null, 0.6);
      [131, 196, 262].forEach((f, i) => this.tone(f, i === 2 ? 0.8 : 0.2, 'triangle', 0.09, null, 0.18 + i * 0.21));
      [2093, 2637, 3136, 4186].forEach((f, i) => this.tone(f, 0.08, 'square', 0.02, null, 1.25 + i * 0.06));
    }
    fall() { this.tone(600, 0.6, 'triangle', 0.06, 80); }
    tick(hi) { this.tone(hi ? 880 : 440, 0.1, 'square', 0.04); }
  }

  // ---------------------------------------------------------------------------
  // Game
  // ---------------------------------------------------------------------------
  class Game {
    constructor(root) {
      this.root = root;
      const ds = root.dataset;
      const list = (s, def) => (s && s.trim() ? s : def).split(',').map((v) => v.trim());
      this.cfg = {
        codes: list(ds.codes, ',,,'),
        percents: list(ds.percents, '5,10,15,20').map((n) => parseInt(n, 10) || 0),
        redirect: ds.redirect || '/collections/all',
        root: (ds.root || '/').replace(/\/$/, ''),
        lives: clamp(parseInt(ds.lives, 10) || 1, 1, 5),
        brand: (ds.brand || 'JAWLINER').toUpperCase(),
        ctaLabel: ds.ctaLabel || 'Redeem discount & shop',
        // Every run needs a player name for the high score list (asked in the menu before the first run).
        // data-leaderboard="off" turns the name entry off.
        leaderboard: ds.leaderboard !== 'off'
      };
      let acc = (ds.accent || '').trim();
      if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(acc)) acc = '#c6ff3d';
      if (acc.length === 4) acc = '#' + acc.slice(1).split('').map((c) => c + c).join('');
      this.accent = acc;
      root.style.setProperty('--jlr-accent', acc);
      root.style.setProperty('--jlr-accent-ink', inkFor(acc));

      this.save = Object.assign({ best: 0, outfit: 0, muted: false }, loadSave());
      if (!OUTFITS[this.save.outfit] || OUTFITS[this.save.outfit].need > this.save.best) this.save.outfit = 0;
      this.viewOutfit = this.save.outfit;

      this.sound = new Sound();
      this.sound.muted = !!this.save.muted;
      if (ds.mode !== 'attract') this.sound.musicUrl = (ds.music || '').trim();
      this.art = buildArt(acc);
      // New York scenery, painted once per stage (needs jawline-run-fighter-scenery.js)
      const SC = window.JawlineRunFighterScenery;
      this.scenery = SC ? SC.create({ text: drawText, brand: this.cfg.brand, accent: acc, pouches: this.art.pouch }) : null;
      if (this.scenery) this.scenery.prepare(0);
      this.frameOutfit = OUTFITS[this.save.outfit];
      this.p = { x: 0, y: 0, vy: 0, grounded: true, coyote: 0, buffer: 0, airT: 0, jumping: false, cutPending: false };
      this.state = 'menu';
      this.level = emptyLevel();
      this.camX = 0; this.time = 0; this.speed = 0;
      this.particles = [];
      this.shake = 0; this.flash = 0;
      this.themeFrom = 0; this.themeTo = 0; this.themeT = 1;
      // Attract mode (data-mode="attract"): no UI, an autopilot plays forever – used as a page background
      this.attract = ds.mode === 'attract';
      if (this.attract) this.sound.muted = true;
      this.psx = PLAYER_SX;

      this.buildDOM();
      this.bind();
      this.resize();
      if (this.attract) this.startAttract();
      else this.updateMenu();
      this.loop = this.loop.bind(this);
      this.visible = true;
      this.updateActive();
    }

    // --- DOM ---------------------------------------------------------------
    buildDOM() {
      const p = this.cfg.percents;
      const r = this.root;
      if (this.attract) {
        r.innerHTML = '<canvas class="jlr-canvas" aria-hidden="true"></canvas>';
        this.el = { cv: r.querySelector('canvas') };
        this.ctx = this.el.cv.getContext('2d');
        this.fx = document.createElement('canvas');
        this.fxc = this.fx.getContext('2d');
        this.confetti = [];
        this.buf = document.createElement('canvas');
        this.b = this.buf.getContext('2d');
        return;
      }
      r.classList.add('jlr', 'jlr--retro', 'jlr--fighter');
      // Frame around the game: none (default), accent or dark – set via data-frame or the Shopify section
      const frame = ['accent', 'dark', 'none'].indexOf(r.dataset.frame) >= 0 ? r.dataset.frame : 'none';
      r.classList.remove('jlr--frame-accent', 'jlr--frame-dark', 'jlr--frame-none');
      r.classList.add('jlr--frame-' + frame);
      r.setAttribute('tabindex', '0');
      r.setAttribute('role', 'application');
      r.setAttribute('aria-label', 'Jawline Run Fighter – mini-game');
      r.innerHTML = `
        <canvas class="jlr-canvas" aria-hidden="true"></canvas>
        <canvas class="jlr-fx" aria-hidden="true"></canvas>
        <div class="jlr-hud" hidden>
          <div class="jlr-hud-row">
            <div class="jlr-stage"><b class="jlr-stage-num">1/4</b><span class="jlr-stage-name">Warm-up</span></div>
            <div class="jlr-chips">
              <span class="jlr-chip jlr-chip-gum"><i class="jlr-gum-ico"></i><b class="jlr-gums">0</b></span>
              <span class="jlr-chip jlr-chip-pct"><b class="jlr-pct">0%</b></span>
            </div>
          </div>
          <div class="jlr-progress">
            ${STAGES.map((s, i) => `<div class="jlr-seg"><div class="jlr-seg-bar"><i></i></div><span>${p[i]}%</span></div>`).join('')}
          </div>
          <div class="jlr-lives"></div>
        </div>
        <div class="jlr-toast" aria-live="polite"></div>
        <div class="jlr-count" aria-hidden="true"></div>
        <button type="button" class="jlr-mute" aria-label="Sound on/off"></button>

        <div class="jlr-screen jlr-menu">
          <div class="jlf-logo"><i></i><span>${this.cfg.brand}</span><i></i></div>
          <h2 class="jlr-title">Jawline<span>Run</span></h2>
          <p class="jlr-sub">Select your runner</p>
          <div class="jlf-roster" role="radiogroup" aria-label="Select your runner">
            ${OUTFITS.map((o, i) => `<button type="button" class="jlf-card" role="radio" data-outfit="${i}">
              <span class="jlf-face"><img src="${(ART.portraits || [])[i] || ''}" alt=""></span>
              <span class="jlf-plate"><b class="jlf-name">${o.name}</b><small class="jlf-tag">${o.tag}</small><b class="jlf-pct">${p[i]}%</b></span>
            </button>`).join('')}
          </div>
          <div class="jlr-outfit-name"></div>
          <form class="jlf-player" novalidate hidden>
            <label class="jlf-player-label" for="jlf-player-${Math.random().toString(36).slice(2, 8)}">Player name for the high scores</label>
            <input class="jlf-player-input" type="text" maxlength="10" autocomplete="nickname" spellcheck="false" autocapitalize="characters" placeholder="YOUR NAME" enterkeyhint="go">
            <small class="jlf-player-hint" aria-live="polite"></small>
          </form>
          <button type="button" class="jlr-btn jlr-start">Press start</button>
          <p class="jlr-hint">Tap to jump · hold for higher · tap again to double jump</p>
        </div>

        <div class="jlr-screen jlr-result" hidden>
          <div class="jlr-kicker jlr-res-kicker"></div>
          <h2 class="jlr-title jlr-res-title"></h2>
          <p class="jlr-res-text"></p>
          <div class="jlr-code-wrap" hidden>
            <div class="jlr-code-label">Your discount code</div>
            <button type="button" class="jlr-code" aria-label="Copy code"></button>
            <small class="jlr-code-hint">Tap to copy</small>
          </div>
          <a class="jlr-btn jlr-cta" href="#" hidden></a>
          <div class="jlr-row">
            <button type="button" class="jlr-btn jlr-ghost jlr-retry">Play again</button>
            <button type="button" class="jlr-btn jlr-ghost jlr-share">Share</button>
          </div>
          <p class="jlr-unlock" hidden></p>
          <form class="jlr-name" hidden novalidate>
            <label class="jlr-name-label" for="jlr-name-${Math.random().toString(36).slice(2, 8)}">Enter your name for the high scores</label>
            <div class="jlr-name-row">
              <input class="jlr-name-input" type="text" maxlength="10" autocomplete="nickname" spellcheck="false" autocapitalize="characters" placeholder="YOUR NAME">
              <button type="submit" class="jlr-btn jlr-name-save">OK</button>
            </div>
            <small class="jlr-name-hint">2–10 letters or numbers</small>
          </form>
          <p class="jlr-name-saved" hidden></p>
          <button type="button" class="jlr-link jlr-back">Back to menu</button>
        </div>

        <div class="jlr-screen jlr-pause" hidden>
          <h2 class="jlr-title jlr-title-sm">Pause</h2>
          <button type="button" class="jlr-btn jlr-resume">Resume</button>
        </div>`;
      const q = (s) => r.querySelector(s);
      this.el = {
        cv: q('.jlr-canvas'), hud: q('.jlr-hud'), stageNum: q('.jlr-stage-num'), stageName: q('.jlr-stage-name'),
        gums: q('.jlr-gums'), pct: q('.jlr-pct'), segs: Array.from(r.querySelectorAll('.jlr-seg')),
        lives: q('.jlr-lives'), toast: q('.jlr-toast'), count: q('.jlr-count'), mute: q('.jlr-mute'),
        menu: q('.jlr-menu'), cards: Array.from(r.querySelectorAll('.jlf-card')), outfitName: q('.jlr-outfit-name'),
        start: q('.jlr-start'),
        result: q('.jlr-result'), resKicker: q('.jlr-res-kicker'), resTitle: q('.jlr-res-title'), resText: q('.jlr-res-text'),
        codeWrap: q('.jlr-code-wrap'), codeLabel: q('.jlr-code-label'), code: q('.jlr-code'), codeHint: q('.jlr-code-hint'),
        cta: q('.jlr-cta'), retry: q('.jlr-retry'), share: q('.jlr-share'), unlock: q('.jlr-unlock'), back: q('.jlr-back'),
        pause: q('.jlr-pause'), resume: q('.jlr-resume'),
        nameForm: q('.jlr-name'), nameInput: q('.jlr-name-input'), nameHint: q('.jlr-name-hint'), nameSaved: q('.jlr-name-saved'),
        player: q('.jlf-player'), playerInput: q('.jlf-player-input'), playerHint: q('.jlf-player-hint')
      };
      this.ctx = this.el.cv.getContext('2d');
      this.fx = r.querySelector('.jlr-fx');
      this.fxc = this.fx.getContext('2d');
      this.confetti = [];
      this.buf = document.createElement('canvas');
      this.b = this.buf.getContext('2d');
      this.renderMute();
    }

    bind() {
      const e = this.el;
      this.on = [];
      const listen = (t, ev, fn, opt) => { t.addEventListener(ev, fn, opt); this.on.push([t, ev, fn, opt]); };
      if (!this.attract) this.bindControls(listen);
      listen(document, 'visibilitychange', () => this.updateActive());

      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this.root);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((en) => {
          this.visible = en[0].isIntersecting;
          this.updateActive();
        }, { threshold: this.attract ? 0 : 0.35 });
        this.io.observe(this.root);
      }
    }

    bindControls(listen) {
      const e = this.el;
      listen(e.start, 'click', () => this.tryStart());
      // Menu name field: Enter starts the run, the name is checked first
      e.playerInput.id = e.player.querySelector('label').htmlFor;
      listen(e.player, 'submit', (ev) => { ev.preventDefault(); this.tryStart(); });
      listen(e.playerInput, 'input', () => { e.playerHint.textContent = ''; e.player.classList.remove('is-error'); });
      listen(e.retry, 'click', () => this.start());
      listen(e.resume, 'click', () => this.resume());
      listen(e.back, 'click', () => this.showMenu());
      listen(e.mute, 'click', () => {
        this.sound.setMuted(!this.sound.muted);
        this.save.muted = this.sound.muted; writeSave(this.save);
        this.sound.init();
        this.renderMute();
      });
      e.cards.forEach((card) => listen(card, 'click', () => {
        const i = Number(card.dataset.outfit);
        this.viewOutfit = i;
        if (OUTFITS[i].need <= this.save.best) {
          this.save.outfit = i; writeSave(this.save);
          this.frameOutfit = OUTFITS[i];
        }
        this.updateMenu();
      }));
      listen(e.code, 'click', () => this.copyCode());
      listen(e.share, 'click', () => this.share());
      listen(e.cta, 'click', () => this.emit('redeem', { code: e.code.textContent }));
      // High score name: label points at the input, submit saves it locally and tells the page
      e.nameInput.id = e.nameForm.querySelector('label').htmlFor;
      listen(e.nameForm, 'submit', (ev) => {
        ev.preventDefault();
        const name = cleanName(e.nameInput.value);
        if (!name) { e.nameHint.textContent = 'Use 2–10 letters or numbers; no links or offensive words.'; e.nameInput.focus(); return; }
        this.save.name = name; writeSave(this.save);
        this.emit('name', { name });
        this.renderName();
      });
      listen(e.nameSaved, 'click', (ev) => {
        if (!ev.target.closest('.jlr-name-change')) return;
        e.nameSaved.hidden = true; e.nameForm.hidden = false;
        e.nameInput.value = this.save.name || ''; e.nameInput.focus();
      });

      // Tap / hold to jump
      listen(e.cv, 'pointerdown', (ev) => { ev.preventDefault(); this.press(); });
      listen(e.cv, 'pointerup', () => this.release());
      listen(e.cv, 'pointercancel', () => this.release());
      listen(e.cv, 'pointerleave', () => this.release());
      const isJump = (k) => k === ' ' || k === 'ArrowUp' || k === 'w' || k === 'W';
      listen(window, 'keydown', (ev) => {
        if (!this.visible || (this.state !== 'run' && this.state !== 'count')) return;
        if (isJump(ev.key)) { ev.preventDefault(); if (!ev.repeat) this.press(); }
        else if ((ev.key === 'Escape' || ev.key === 'p') && this.state === 'run') this.pause();
      });
      listen(window, 'keyup', (ev) => { if (isJump(ev.key)) this.release(); });
    }

    destroy() {
      cancelAnimationFrame(this.raf);
      this.raf = null;
      this.on.forEach(([t, ev, fn, opt]) => t.removeEventListener(ev, fn, opt));
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      if (this.sound.ctx) this.sound.ctx.close();
      delete this.root.__jlrFighter;
    }

    emit(name, detail) {
      if (this.attract) return;
      detail = Object.assign({ variant: 'fighter' }, detail || {});
      this.root.dispatchEvent(new CustomEvent('jawlinerun:' + name, { bubbles: true, detail }));
      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('jawline_run_' + name, detail);
      } catch (err) { /* optional */ }
    }

    resize() {
      const r = this.root.getBoundingClientRect();
      const W = Math.max(200, r.width), H = Math.max(240, r.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = Math.round(W * dpr), ch = Math.round(H * dpr);
      this.el.cv.width = cw; this.el.cv.height = ch;
      this.fx.width = cw; this.fx.height = ch;
      // Close camera: VIEW_H world pixels from top to bottom (a wide page background shows a bit more)
      this.sc = ch / (this.attract ? VIEW_H * 1.35 : VIEW_H);
      this.iw = Math.ceil(cw / this.sc);
      this.ih = Math.ceil(ch / this.sc);
      this.cu = Math.max(2, Math.round(this.sc / 2.4)); // confetti pixel size in device pixels
      this.psx = this.attract ? Math.round(this.iw * 0.2) : 16;
      this.buf.width = this.iw * RES; this.buf.height = this.ih * RES;
      this.gy = this.ih - FLOOR_H;
      this.camY = this.camY || 0;
      if (!this.raf) this.render();
    }

    // Only runs while the game is visible (saves battery, pauses when scrolled away)
    updateActive() {
      const active = this.visible && !document.hidden;
      if (active && !this.raf) {
        this.last = performance.now();
        this.raf = requestAnimationFrame(this.loop);
      } else if (!active && this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
        if (!this.attract && (this.state === 'run' || this.state === 'count')) this.pause();
      }
    }

    startAttract() {
      this.attractOutfit = ((this.attractOutfit == null ? -1 : this.attractOutfit) + 1) % OUTFITS.length;
      this.frameOutfit = OUTFITS[this.attractOutfit];
      this.level = buildLevel();
      Object.assign(this.p, { x: 0, y: 0, vy: 0, grounded: true, coyote: 0, buffer: 0, airT: 0, jumping: false, cutPending: false, dbl: false, wantDouble: false, spin: 0 });
      this.held = false; this.holdUntil = 0;
      this.safeX = 0;
      this.camX = -this.psx;
      this.stage = 0; this.unlocked = 0; this.gums = 0; this.gumNext = 0; this.eat = null;
      this.speed = STAGES[0].speed;
      this.lives = 99; this.invuln = 0;
      this.particles = [];
      this.setTheme(0, true);
      this.state = 'run';
    }

    // Simple bot for attract mode: jumps pits, boxes and enemies, shows off a double jump over pits
    autopilot() {
      const p = this.p, L = this.level;
      if (this.held && this.time > this.holdUntil) this.release();
      if (!p.grounded) {
        if (!p.dbl && p.vy < 0 && p.y < 30 && !this.onGround(p.x, p.x + PW + 24)) { this.release(); this.press(); this.holdUntil = this.time + 0.25; }
        return;
      }
      const front = p.x + PW;
      let need = !this.onGround(front - 2, front) && p.y === 0;
      for (const b of L.blocks) { if (b.x >= front - 1 && b.x - front < this.speed * 0.13 && b.h > p.y) { need = true; break; } }
      for (const en of L.enemies) { if (!en.dead && en.x + 14 > front - 4 && en.x - front < this.speed * 0.22) { need = true; break; } }
      if (need) { this.press(); this.holdUntil = this.time + 0.3; }
    }

    // --- Flow --------------------------------------------------------------
    // Start from the menu: with the high score list on, a valid player name is required first
    tryStart() {
      const e = this.el;
      if (this.cfg.leaderboard && !this.attract) {
        const name = cleanName(e.playerInput.value);
        if (!name) {
          e.player.classList.add('is-error');
          e.playerHint.textContent = e.playerInput.value.trim() ? 'Use 2–10 letters or numbers; no links or offensive words.' : 'Enter your name to start.';
          e.playerInput.focus();
          return;
        }
        if (name !== this.save.name) { this.save.name = name; writeSave(this.save); this.emit('name', { name }); }
        e.playerInput.value = name;
      }
      this.start();
    }

    start() {
      this.sound.init();
      this.level = buildLevel();
      this.frameOutfit = OUTFITS[this.save.outfit] || OUTFITS[0];
      Object.assign(this.p, { x: 0, y: 0, vy: 0, grounded: true, coyote: 0, buffer: 0, airT: 0, jumping: false, cutPending: false, dbl: false, wantDouble: false, spin: 0 });
      this.held = false;
      this.safeX = 0;
      this.camX = -this.psx;
      this.stage = 0; this.unlocked = 0; this.gums = 0; this.gumNext = 0; this.eat = null;
      this.speed = STAGES[0].speed;
      this.lives = this.cfg.lives; this.invuln = 0;
      this.playT = 0;
      this.particles = [];
      this.setTheme(0, true);
      this.el.menu.hidden = true;
      this.el.result.hidden = true;
      this.el.hud.hidden = false;
      this.hudCache = {};
      this.renderLives();
      this.countdown();
      this.root.focus({ preventScroll: true });
      this.emit('start');
    }

    countdown() {
      this.state = 'count';
      this.countT = 3;
      this.lastCount = 0;
    }

    hideCount() {
      this.goT = 0;
      this.el.count.classList.remove('is-on');
      this.el.count.textContent = '';
    }

    pause() {
      this.sound.playMusic(false);
      this.hideCount();
      this.release();
      this.state = 'paused';
      this.el.pause.hidden = false;
    }

    resume() {
      this.sound.init();
      this.el.pause.hidden = true;
      this.countdown();
      this.root.focus({ preventScroll: true });
    }

    showMenu() {
      this.sound.playMusic(false);
      this.state = 'menu';
      this.el.result.hidden = true;
      this.el.hud.hidden = true;
      this.el.menu.hidden = false;
      this.level = emptyLevel();
      Object.assign(this.p, { y: 0, vy: 0, grounded: true });
      this.setTheme(0, true);
      this.updateMenu();
    }

    press() {
      this.held = true;
      if (this.state !== 'run') return;
      const p = this.p;
      // In the air with the double jump unused: jump again. Otherwise remember the tap for the landing.
      if (!p.grounded && p.coyote <= 0 && !p.dbl) p.wantDouble = true;
      else p.buffer = PHY.BUFFER;
    }

    release() {
      this.held = false;
      const p = this.p;
      if (p.jumping && p.vy > PHY.CUT) {
        if (p.airT >= PHY.MIN_HOLD) p.vy = PHY.CUT;
        else p.cutPending = true;
      }
    }

    setTheme(i, instant) {
      this.themeFrom = instant ? i : this.themeTo;
      this.themeTo = i;
      this.themeT = instant ? 1 : 0;
    }

    completeStage() {
      const i = this.stage;
      if (this.attract) {
        this.unlocked = i + 1;
        this.stage++;
        if (this.stage >= STAGES.length) { this.state = 'finishing'; this.finishT = 0; }
        else this.setTheme(this.stage);
        for (let k = 0; k < 30; k++) this.burst(this.level.bounds[i], 50, pick([this.accent, '#ffffff', GOLD]), 1.4);
        return;
      }
      this.unlocked = i + 1;
      this.stage++;
      const pct = this.cfg.percents[i];
      this.emit('stage', { stage: i + 1, name: STAGES[i].name, percent: pct });
      this.sound.stage();
      if (this.stage >= STAGES.length) {
        this.toast(`Finish! ${pct}% unlocked`, 2.2);
        this.state = 'finishing';
        this.finishT = 0;
      } else {
        this.toast(`Stage ${i + 1} clear!<small>${pct}% unlocked</small>`, 1.8);
        this.setTheme(this.stage);
      }
      const gx = this.level.bounds[i];
      for (let k = 0; k < 30; k++) this.burst(gx, 50, pick([this.accent, '#ffffff', GOLD]), 1.4);
    }

    hit() {
      if (this.attract || this.invuln > 0 || this.state !== 'run') return;
      this.lives--;
      this.renderLives();
      this.flash = 1;
      this.shake = 1;
      this.release();
      if (this.lives > 0) {
        this.sound.stomp();
        this.invuln = 1.6;
        this.p.vy = 260; this.p.grounded = false; this.p.jumping = false; this.p.dbl = false;
        this.toast(`Ouch! ${this.lives} ${this.lives === 1 ? 'life' : 'lives'} left`, 1.2);
      } else {
        this.sound.playMusic(false);
        this.sound.gameOver();
        this.state = 'crash';
        this.crashT = 0;
        this.fell = false;
        this.p.vy = 300;
      }
    }

    fall() {
      if (this.state !== 'run') return;
      if (this.attract) { Object.assign(this.p, { x: this.safeX, y: 0, vy: 0, grounded: true, jumping: false, dbl: false, spin: 0 }); return; }
      this.lives--;
      this.renderLives();
      this.sound.fall();
      this.release();
      if (this.lives > 0) {
        Object.assign(this.p, { x: this.safeX, y: 0, vy: 0, grounded: true, jumping: false, dbl: false, spin: 0 });
        this.invuln = 1.6;
        this.toast(`Watch the gap! ${this.lives} ${this.lives === 1 ? 'life' : 'lives'} left`, 1.2);
      } else {
        this.sound.playMusic(false);
        this.sound.gameOver(0.55);
        this.state = 'crash';
        this.crashT = 0;
        this.fell = true;
      }
    }

    finish(won) {
      this.sound.playMusic(false);
      this.state = 'over';
      this.el.hud.hidden = true;
      clearTimeout(this.toastTimer);
      this.el.toast.classList.remove('is-on');
      this.release();
      const got = this.unlocked, p = this.cfg.percents, codes = this.cfg.codes;
      const prevBest = this.save.best || 0;
      this.save.best = Math.max(prevBest, got);
      const fresh = OUTFITS.filter((o) => o.need > prevBest && o.need <= got);
      if (fresh.length) {
        this.save.outfit = OUTFITS.indexOf(fresh[fresh.length - 1]);
        this.frameOutfit = OUTFITS[this.save.outfit];
      }
      writeSave(this.save);
      this.emit('end', { percent: got ? p[got - 1] : 0, stages: got, gums: this.gums, won, seconds: Math.round(this.playT || 0), name: this.save.name || '' });

      const e = this.el;
      const gumTxt = `${this.gums} gum pack${this.gums === 1 ? '' : 's'} eaten.`;
      if (won) {
        e.resKicker.textContent = 'Perfect · all 4 stages';
        e.resTitle.innerHTML = `You win!<span>${p[got - 1]}% unlocked</span>`;
        e.resText.textContent = `Full course, no game over. ${gumTxt}`;
      } else {
        e.resKicker.textContent = `Stopped in stage ${got + 1} · ${STAGES[got].name}`;
        e.resTitle.innerHTML = 'K.O.';
        e.resText.textContent = got
          ? `You unlocked ${p[got - 1]}% off. ${gumTxt}`
          : `So close! Clear the warm-up to get ${p[0]}% off. ${gumTxt}`;
      }
      // Code: the best discount ever reached
      const best = this.save.best;
      const code = best ? codes[best - 1] : '';
      e.codeWrap.hidden = !best;
      if (best) {
        e.codeLabel.textContent = best > got ? `Your best discount · ${p[best - 1]}%` : `Your discount code · ${p[best - 1]}%`;
        e.code.textContent = code || 'Code missing';
        e.codeHint.textContent = code ? 'Tap to copy' : 'Please add the codes in the theme editor.';
      }
      e.cta.hidden = !code;
      if (code) {
        e.cta.textContent = this.cfg.ctaLabel;
        e.cta.href = `${this.cfg.root}/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(this.cfg.redirect)}`;
      }
      e.retry.textContent = best < STAGES.length ? `Go again for ${p[best]}%` : 'Run again';
      e.share.hidden = !got;
      e.unlock.hidden = !fresh.length;
      if (fresh.length) e.unlock.textContent = `New outfit unlocked: ${fresh.map((o) => o.name).join(', ')}`;
      this.renderName();
      e.result.classList.toggle('is-win', !!won);
      e.result.hidden = false;
      if (won) { this.sound.win(); this.throwConfetti(); }
    }

    // --- UI ----------------------------------------------------------------
    toast(html, dur) {
      const t = this.el.toast;
      t.innerHTML = html;
      t.classList.add('is-on');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => t.classList.remove('is-on'), (dur || 1.5) * 1000);
    }

    // Pixel speaker on a 16×16 grid: sound waves when on, an X when muted
    renderMute() {
      const r = (x, y, w, h) => `<rect x="${x}" y="${y}" width="${w}" height="${h}"/>`;
      const speaker = r(2, 6, 3, 4) + r(5, 5, 1, 6) + r(6, 4, 1, 8) + r(7, 3, 1, 10) + r(8, 2, 1, 12);
      const waves = r(10, 6, 1, 4) + r(11, 5, 1, 1) + r(11, 10, 1, 1) + r(12, 4, 1, 1) + r(12, 11, 1, 1) + r(13, 5, 1, 6);
      const cross = [10, 11, 12, 13, 14].map((v, i) => r(v, 5 + i, 1, 1) + r(v, 9 - i, 1, 1)).join('');
      this.el.mute.innerHTML = `<svg viewBox="0 0 16 16" width="20" height="20" shape-rendering="crispEdges" fill="currentColor" aria-hidden="true">${speaker}${this.sound.muted ? cross : waves}</svg>`;
      this.el.mute.classList.toggle('is-muted', this.sound.muted);
      this.el.mute.setAttribute('aria-pressed', String(!this.sound.muted));
    }

    // Result screen: name form until a name is saved, then "on the board as NAME · change"
    renderName() {
      const e = this.el;
      if (!this.cfg.leaderboard || this.attract) { e.nameForm.hidden = true; e.nameSaved.hidden = true; return; }
      const name = this.save.name;
      e.nameForm.hidden = !!name;
      e.nameSaved.hidden = !name;
      e.nameHint.textContent = '2–10 letters or numbers';
      if (name) {
        e.nameSaved.textContent = '';
        e.nameSaved.append('On the high scores as ', Object.assign(document.createElement('b'), { textContent: name }), ' · ');
        const ch = document.createElement('button');
        ch.type = 'button'; ch.className = 'jlr-link jlr-name-change'; ch.textContent = 'change';
        e.nameSaved.append(ch);
      }
    }

    renderLives() {
      if (this.cfg.lives <= 1) { this.el.lives.innerHTML = ''; return; }
      let h = '';
      for (let i = 0; i < this.cfg.lives; i++) h += `<i class="${i < this.lives ? 'on' : ''}"></i>`;
      this.el.lives.innerHTML = h;
    }

    updateMenu() {
      const best = this.save.best || 0, p = this.cfg.percents, e = this.el;
      e.player.hidden = !this.cfg.leaderboard || this.attract;
      if (!e.player.hidden && document.activeElement !== e.playerInput) e.playerInput.value = this.save.name || '';
      this.el.cards.forEach((card, i) => {
        const o = OUTFITS[i], locked = o.need > best, sel = i === this.save.outfit;
        card.classList.toggle('is-locked', locked);
        card.classList.toggle('is-selected', sel);
        card.setAttribute('aria-checked', String(sel));
        card.setAttribute('aria-label', locked ? `${o.name}, locked: reach ${p[o.need - 1]}%` : `${o.name}, ${o.tag}`);
      });
      const o = OUTFITS[this.viewOutfit] || OUTFITS[0];
      const locked = o.need > best;
      this.el.outfitName.innerHTML = locked
        ? `<b>${o.name}</b> · reach ${p[o.need - 1]}% to unlock`
        : `<b>${o.name}</b> · ${o.tag}${this.viewOutfit === this.save.outfit ? ' · selected' : ''}`;
      this.el.outfitName.classList.toggle('is-locked', locked);
    }

    updateHUD() {
      const h = this.hudCache, e = this.el;
      const si = Math.min(this.stage, STAGES.length - 1);
      if (h.stage !== si) {
        h.stage = si;
        e.stageNum.textContent = `${si + 1}/${STAGES.length}`;
        e.stageName.textContent = STAGES[si].name;
      }
      if (h.gums !== this.gums) { h.gums = this.gums; e.gums.textContent = this.gums; }
      if (h.unl !== this.unlocked) {
        h.unl = this.unlocked;
        e.pct.textContent = `${this.unlocked ? this.cfg.percents[this.unlocked - 1] : 0}%`;
        e.pct.parentNode.classList.toggle('is-on', this.unlocked > 0);
      }
      const { starts, bounds } = this.level;
      e.segs.forEach((s, i) => {
        const f = clamp((this.p.x - starts[i]) / (bounds[i] - starts[i]), 0, 1);
        s.firstElementChild.firstElementChild.style.transform = `scaleX(${f})`;
        s.classList.toggle('done', i < this.unlocked);
      });
    }

    copyCode() {
      const code = this.el.code.textContent;
      if (!code || code === 'Code missing') return;
      const done = () => { this.el.codeHint.textContent = 'Copied ✓'; };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, () => this.fallbackCopy(code, done));
      else this.fallbackCopy(code, done);
    }

    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;opacity:0;left:-9999px';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (err) { /* ignore */ }
      ta.remove();
    }

    share() {
      const pct = this.unlocked ? this.cfg.percents[this.unlocked - 1] : 0;
      const text = `I unlocked ${pct}% in JAWLINE RUN. Can you get ${this.cfg.percents[3]}%?`;
      const url = location.href.split('#')[0];
      const copy = () => this.fallbackCopy(`${text} ${url}`, () => this.toast('Link copied', 1.2));
      if (navigator.share) navigator.share({ title: 'Jawline Run', text, url }).catch((err) => { if (!err || err.name !== 'AbortError') copy(); });
      else copy();
    }

    // --- Game loop ---------------------------------------------------------
    loop(ts) {
      const dt = Math.min(0.05, Math.max(0, (ts - this.last) / 1000));
      this.last = ts;
      this.update(dt);
      this.render();
      this.raf = requestAnimationFrame(this.loop);
    }

    update(dt) {
      this.time += dt;
      // Time spent playing (countdown, run, finish line, crash), summed for the high scores
      if (this.state === 'count' || this.state === 'run' || this.state === 'finishing' || this.state === 'crash') this.playT = (this.playT || 0) + dt;
      if (this.themeT < 1) this.themeT = Math.min(1, this.themeT + dt * 0.8);
      if (this.goT > 0 && (this.goT -= dt) <= 0) this.hideCount();
      const p = this.p;

      switch (this.state) {
        case 'menu':
        case 'over':
          this.camX += 40 * dt;
          p.x = this.camX + this.psx;
          p.y = 0; p.grounded = true;
          break;
        case 'count': {
          this.countT -= dt;
          const n = Math.ceil(this.countT);
          if (n !== this.lastCount) {
            this.lastCount = n;
            this.el.count.textContent = n > 0 ? n : 'Run!';
            this.el.count.classList.remove('is-on'); void this.el.count.offsetWidth; this.el.count.classList.add('is-on');
            this.sound.tick(n <= 0);
          }
          if (this.countT <= 0) {
            this.state = 'run'; this.goT = 0.7;
            if (!this.attract) this.sound.playMusic(true); // music starts with "Run!"
            if (this.held) p.buffer = PHY.BUFFER;
            if (!this.save.djTip) this.toast('Tap again in the air<small>for a double jump</small>', 2.2);
          }
          break;
        }
        case 'run': {
          if (this.attract) this.autopilot();
          const steps = Math.ceil(dt / (1 / 240));
          for (let i = 0; i < steps && this.state === 'run'; i++) this.step(dt / steps);
          if (this.invuln > 0) this.invuln -= dt;
          this.camX = p.x - this.psx;
          break;
        }
        case 'finishing':
          p.x += this.speed * dt;
          this.camX = p.x - this.psx;
          this.finishT += dt;
          if (this.finishT > 1.5) { if (this.attract) this.startAttract(); else this.finish(true); }
          break;
        case 'crash':
          this.crashT += dt;
          if (!this.fell) { p.vy -= PHY.G * dt; p.y += p.vy * dt; }
          if (this.crashT > (this.fell ? 1.6 : 2.0)) this.finish(false); // let the game-over jingle play
          break;
      }
      if (this.state === 'run' || this.state === 'finishing') {
        this.updateEnemies(dt);
        if (!this.attract) this.updateHUD();
      }
      if (this.punchT > 0) this.punchT -= dt;
      // Camera rises with high jumps so the runner's head stays in view
      const wantY = this.state === 'run' || this.state === 'finishing' ? Math.max(0, p.y + 62 - this.gy) : 0; // keep the head below the HUD
      this.camY = (this.camY || 0) + (wantY - (this.camY || 0)) * Math.min(1, dt * 7);
      this.updateConfetti(dt);
      this.updateEat(dt);
      this.shake = Math.max(0, this.shake - dt * 3);
      this.flash = Math.max(0, this.flash - dt * 3);
      for (let i = this.particles.length - 1; i >= 0; i--) {
        const q = this.particles[i];
        q.life -= dt;
        if (q.life <= 0) { this.particles.splice(i, 1); continue; }
        q.x += q.vx * dt; q.y += q.vy * dt; q.vy -= 500 * dt * (q.g || 0);
      }
    }

    onGround(x0, x1) {
      for (const s of this.level.ground) if (x1 > s[0] && x0 < s[1]) return true;
      return false;
    }

    step(dt) {
      const p = this.p, L = this.level;
      const S = STAGES[Math.min(this.stage, STAGES.length - 1)];
      this.speed += (S.speed - this.speed) * Math.min(1, dt * 1.5);
      p.x += this.speed * dt;
      p.airT += dt;
      if (p.buffer > 0) p.buffer -= dt;
      if (p.coyote > 0) p.coyote -= dt;

      // Jump (with buffer and coyote time, so slightly early/late taps still count)
      if (p.buffer > 0 && (p.grounded || p.coyote > 0)) {
        p.vy = PHY.JUMP; p.jumping = true; p.grounded = false;
        p.airT = 0; p.buffer = 0; p.coyote = 0;
        p.cutPending = !this.held;
        this.sound.jump();
      }
      if (p.wantDouble) {
        p.wantDouble = false;
        p.vy = PHY.DJUMP; p.jumping = true; p.dbl = true;
        p.airT = 0; p.buffer = 0;
        p.cutPending = !this.held;
        p.spin = PHY.SPIN;
        this.sound.double();
        for (let k = 0; k < 6; k++) this.particles.push({ x: p.x + 6 + rand(-5, 5), y: p.y, vx: rand(-30, 30), vy: rand(-20, 10), life: 0.35, max: 0.35, color: '#ffffff' });
        if (!this.attract && !this.save.djTip) { this.save.djTip = true; writeSave(this.save); }
      }
      if (p.spin > 0) p.spin -= dt;
      if (p.cutPending && p.airT >= PHY.MIN_HOLD) {
        if (p.vy > PHY.CUT) p.vy = PHY.CUT;
        p.cutPending = false;
      }

      // Running into a box from the side; small ledges are stepped up automatically
      if (this.invuln <= 0) {
        for (const b of L.blocks) {
          if (b.x > p.x + PW) break;
          if (p.x + PW > b.x && p.x < b.x + b.w && p.y < b.h - 0.01) {
            if (b.h - p.y <= 8) { p.y = b.h; if (p.vy < 0) p.vy = 0; } // forgiving corners
            else { this.hit(); return; }
          }
        }
      }

      // Gravity and landing
      const prevY = p.y, wasGrounded = p.grounded;
      p.vy -= PHY.G * dt;
      p.y += p.vy * dt;
      p.grounded = false;
      if (p.vy <= 0) {
        let top = -Infinity;
        if (prevY >= -0.5 && this.onGround(p.x, p.x + PW)) top = 0;
        for (const b of L.blocks) {
          if (b.x > p.x + PW) break;
          if (p.x + PW > b.x && p.x < b.x + b.w && prevY >= b.h - 0.5) top = Math.max(top, b.h);
        }
        for (const pl of L.plats) {
          if (p.x + PW > pl.x && p.x < pl.x + pl.w && prevY >= pl.y - 0.5) top = Math.max(top, pl.y);
        }
        if (top > -Infinity && p.y <= top) {
          p.y = top; p.vy = 0; p.grounded = true; p.jumping = false; p.dbl = false; p.spin = 0;
        }
      }
      if (wasGrounded && !p.grounded && !p.jumping) p.coyote = PHY.COYOTE;
      // Remember a safe spot to respawn after a fall (only with extra lives)
      if (p.grounded && p.y === 0 && L.ground.some((s) => p.x - 24 >= s[0] && p.x + PW + 8 <= s[1])) this.safeX = p.x;
      if (p.y < -70) { this.fall(); return; }

      // Gum pouches: the runner grabs them and eats them
      while (this.gumNext < L.gums.length && L.gums[this.gumNext].x < p.x - 10) this.gumNext++;
      for (let i = this.gumNext; i < L.gums.length; i++) {
        const g = L.gums[i];
        if (g.x > p.x + PW + 8) break;
        if (!g.taken && g.x + 6 > p.x && g.x - 6 < p.x + PW && g.y + 8 > p.y && g.y - 8 < p.y + PH) {
          g.taken = true;
          this.gums++;
          this.sound.chomp();
          this.eat = { t: 0, f: g.f, x: g.x, y: g.y, popped: false };
          this.particles.push({ x: g.x, y: g.y + 12, vx: 0, vy: 30, life: 0.7, max: 0.7, text: '+1' });
        }
      }

      // Enemies: stomp from above, anything else hurts
      for (const en of L.enemies) {
        if (en.dead || en.x > p.x + 40) continue;
        if (en.x + 14 < p.x - 40) continue;
        const eh = en.type === 'hopper' ? 14 : 12;
        if (en.x + 13 > p.x && en.x + 1 < p.x + PW && en.y + eh > p.y && en.y < p.y + PH) {
          if (p.vy < 0 && prevY >= en.y + eh - 7) {
            en.dead = 0.4;
            p.vy = 250; p.jumping = true; p.airT = PHY.MIN_HOLD; p.cutPending = !this.held; p.dbl = false;
            this.punchT = 0.35;
            this.sound.stomp();
            for (let k = 0; k < 8; k++) this.burst(en.x + 7, en.y + 6, '#ffffff', 0.6);
          } else if (this.invuln <= 0) {
            this.hit();
            return;
          }
        }
      }

      while (this.stage < STAGES.length && p.x >= L.bounds[this.stage]) {
        this.completeStage();
        if (this.state !== 'run') return;
      }
    }

    updateEnemies(dt) {
      const near = this.camX + this.iw + 80;
      for (const en of this.level.enemies) {
        if (en.dead) { en.dead -= dt; if (en.dead <= 0) en.dead = -1; continue; }
        if (en.x > near || en.x < this.camX - 40) continue;
        if (en.type === 'walker') {
          en.x += en.dir * 22 * dt;
          if (en.x < en.x0) { en.x = en.x0; en.dir = 1; }
          if (en.x > en.x1) { en.x = en.x1; en.dir = -1; }
        } else {
          en.y = Math.abs(Math.sin(this.time * 2.6 + en.phase)) * 46; // high enough to run under on the way up
        }
      }
    }

    // Eating: pouch flies into the mouth (0–0.12 s), chewing (to 0.45 s), bubble grows (to 0.75 s) and pops
    updateEat(dt) {
      const e = this.eat;
      if (!e) return;
      e.t += dt;
      if (!e.popped && e.t >= 0.75) {
        e.popped = true;
        this.sound.pop();
        const m = this.mouth();
        const col = FLAVORS[e.f].a;
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          this.particles.push({ x: m.x + 7, y: m.y, vx: Math.cos(a) * 60, vy: Math.sin(a) * 60, life: 0.3, max: 0.3, color: col });
        }
      }
      if (e.t > 0.8) this.eat = null;
    }

    // Mouth position in world coordinates (the runner faces right)
    mouth() {
      const R = ART.runner, o = this.frameOutfit || OUTFITS[this.save.outfit] || OUTFITS[0];
      const m = (R && R.mouth && R.mouth[o.row]) || [80, 44];
      return { x: this.p.x + 6 + (m[0] - R.hipX) / RES, y: this.p.y + (R.feet - m[1]) / RES };
    }

    drawEat(cam) {
      const e = this.eat, b = this.b, p = this.p;
      if (!e || p.spin > 0 || this.state === 'crash') return;
      const m = this.mouth(), mx = Math.round((m.x - cam) * RES) / RES, my = Math.round((this.gy - m.y) * RES) / RES;
      const fl = FLAVORS[e.f];
      if (e.t < 0.12) {
        // Pouch flies into the mouth and shrinks
        const k = e.t / 0.12, s = 1 - k * 0.7;
        const x = (e.x - cam) + (mx - (e.x - cam)) * k, y = (this.gy - e.y) + (my - (this.gy - e.y)) * k;
        b.drawImage(this.art.pouch[e.f], x - 3.75 * s, y - 4.25 * s, 7.5 * s, 8.5 * s);
      } else if (e.t < 0.45) {
        // Chewing: the jaw opens and closes
        if (Math.floor(e.t * 18) % 2) { b.fillStyle = '#3a1a10'; b.fillRect(mx - 1, my - 0.25, 1.25, 0.75); }
        b.fillStyle = fl.a; b.fillRect(mx - 0.5, my + 0.25, 0.5, 0.5);
      }
      if (e.t >= 0.4 && e.t < 0.75) {
        // Bubble in the flavor color, like on the packaging
        const k = Math.min(1, (e.t - 0.4) / 0.3), r = 0.75 + Math.round(k * 9) / 4;
        const cx = mx + r, cy = my;
        for (let dy = -r - 0.25; dy <= r + 0.25; dy += 0.25) for (let dx = -r - 0.25; dx <= r + 0.25; dx += 0.25) {
          const d = dx * dx + dy * dy;
          if (d > (r + 0.2) * (r + 0.2)) continue;
          const l = -dx * 0.6 - dy * 0.8;
          b.fillStyle = d > (r - 0.25) * (r - 0.25) ? shade(fl.a, 0.4) : l > r * 0.55 ? '#ffffff' : l > r * 0.12 ? fl.a : shade(fl.a, 0.72);
          b.fillRect(cx + dx, cy + dy, 0.25, 0.25);
        }
      }
    }

    // Pixel confetti on a layer above the result screen
    throwConfetti() {
      const W = this.fx.width, H = this.fx.height, u = this.cu;
      const cols = [this.accent, GOLD, '#ffffff', '#ff4fa0', '#3d8bff', '#ff8a1f'];
      for (let i = 0; i < 200; i++) {
        this.confetti.push({
          x: rand(0, W), y: rand(-H * 1.4, -10), vx: rand(-40, 40) * u, vy: rand(40, 100) * u,
          s: u * (Math.random() < 0.3 ? 3 : 2), c: pick(cols), ph: rand(0, 6), sp: rand(4, 9), life: rand(5, 7)
        });
      }
    }

    updateConfetti(dt) {
      const list = this.confetti;
      if (!list.length && !this.fxDirty) return;
      const c = this.fxc, W = this.fx.width, H = this.fx.height;
      c.clearRect(0, 0, W, H);
      this.fxDirty = list.length > 0;
      for (let i = list.length - 1; i >= 0; i--) {
        const q = list[i];
        q.life -= dt;
        q.ph += q.sp * dt;
        q.vy = Math.min(q.vy + 80 * this.cu * dt, 100 * this.cu);
        q.x += (q.vx + Math.sin(q.ph) * 30 * this.cu) * dt;
        q.y += q.vy * dt;
        if (q.life <= 0 || q.y > H + 10) { list.splice(i, 1); continue; }
        const flip = Math.abs(Math.cos(q.ph)); // flutter: the piece turns edge-on now and then
        c.globalAlpha = Math.min(1, q.life);
        c.fillStyle = q.c;
        c.fillRect(Math.round(q.x), Math.round(q.y), Math.max(this.cu, Math.round(q.s * 2 * flip)), q.s * 2);
      }
      c.globalAlpha = 1;
    }

    burst(x, y, color, power) {
      const a = Math.random() * Math.PI * 2, v = rand(40, 120) * power;
      this.particles.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v + 40, life: rand(0.4, 0.9), max: 0.9, g: 1, color });
    }

    theme() {
      const a = THEMES[this.themeFrom], b = THEMES[this.themeTo], t = this.themeT;
      const o = {};
      for (const k in a) o[k] = Array.isArray(a[k]) ? a[k].map((c, i) => mixHex(c, b[k][i], t)) : typeof a[k] === 'number' ? a[k] + (b[k] - a[k]) * t : mixHex(a[k], b[k], t);
      return o;
    }

    // --- Rendering (into the low-res buffer, then scaled up crisp) ---------
    render() {
      const gy0 = this.gy;
      this.gy = gy0 + Math.round((this.camY || 0) * RES) / RES;
      try { this.renderScene(); } finally { this.gy = gy0; }
    }

    renderScene() {
      const b = this.b, iw = this.iw, ih = this.ih, gy = this.gy;
      if (!iw) return;
      const th = this.theme();
      const cam = Math.round(this.camX + (this.shake > 0 ? (Math.random() - 0.5) * this.shake * 6 : 0));
      b.setTransform(RES, 0, 0, RES, 0, 0); // draw in world pixels; sprites keep their double density
      b.imageSmoothingEnabled = false;

      this.paintBackground(cam, th);
      this.paintFloor(cam, th);
      if (this.scenery) this.scenery.fx(b, this.themeFrom, this.themeTo, this.themeT, cam, gy - FLOOR_BACK, iw, this.time, this.accent);
      const L = this.level;

      // Checkpoint gates
      L.bounds.forEach((gx, i) => {
        const sx = Math.round(gx - cam);
        if (this.state === 'menu' || this.state === 'over' || sx < -40 || sx > iw + 40) return;
        const last = i === STAGES.length - 1, col = last ? GOLD : this.accent;
        const label = last ? `FINISH ${this.cfg.percents[i]}%` : `${this.cfg.percents[i]}% OFF`;
        const hw = Math.max(20, (textW(label) >> 1) + 5), top = gy - 50;
        b.fillStyle = '#1a1008'; b.fillRect(sx - hw + 1, top, 3, 50); b.fillRect(sx + hw - 4, top, 3, 50);
        b.fillStyle = '#8a5a34'; b.fillRect(sx - hw + 1.5, top, 1.5, 50); b.fillRect(sx + hw - 3.5, top, 1.5, 50);
        b.fillStyle = '#0b0d1c'; b.fillRect(sx - hw - 1, top - 4, hw * 2 + 2, 12);
        b.fillStyle = col;
        b.fillRect(sx - hw, top - 3, hw * 2, 0.5); b.fillRect(sx - hw, top + 6.5, hw * 2, 0.5);
        b.fillRect(sx - hw - 0.5, top - 2.5, 0.5, 9); b.fillRect(sx + hw, top - 2.5, 0.5, 9);
        drawText(b, label, sx - (textW(label) >> 1), top - 0.5, col, 1, '#000000');
        if (last) for (let k = 0; k < 8; k++) { b.fillStyle = k % 2 ? '#ffffff' : '#14141a'; b.fillRect(sx - 16 + k * 4, gy, 4, 2); }
      });

      // Platforms and boxes
      for (const pl of L.plats) {
        const sx = Math.round(pl.x - cam);
        if (sx > iw || sx + pl.w < 0) continue;
        for (let k = 0; k < pl.w; k += T) b.drawImage(this.art.beam, sx + k, gy - pl.y, 16, 7);
      }
      for (const bl of L.blocks) {
        const sx = Math.round(bl.x - cam);
        if (sx > iw) break;
        if (sx + bl.w < 0) continue;
        for (let k = 0; k < bl.w; k += T) for (let h = 0; h < bl.h; h += T) b.drawImage(this.art.box, sx + k, gy - h - T, 16, 16);
      }

      // Gum packs
      for (const g of L.gums) {
        if (g.taken) continue;
        const sx = Math.round(g.x - cam);
        if (sx < -10) continue;
        if (sx > iw + 10) break;
        const bob = Math.round(Math.sin(this.time * 5 + g.x * 0.05) * 2) / 2;
        b.drawImage(this.art.pouch[g.f || 0], sx - 3.75, gy - g.y - 4.25 + bob, 7.5, 8.5);
        if (Math.sin(this.time * 4 + g.x) > 0.85) { b.fillStyle = '#ffffff'; b.fillRect(sx + 3, gy - g.y - 5.5 + bob, 0.25, 1.25); b.fillRect(sx + 2.5, gy - g.y - 5 + bob, 1.25, 0.25); }
      }

      // Enemies
      for (const en of L.enemies) {
        if (en.dead === -1) continue;
        const sx = Math.round(en.x - cam);
        if (sx < -20 || sx > iw + 20) continue;
        if (en.type === 'walker') {
          const img = this.art.bot[Math.floor(this.time * 6) % 2];
          if (en.dead > 0) { b.drawImage(img, sx, gy - 6, 16, 6); continue; }
          b.save();
          if (en.dir > 0) { b.translate(sx + 16, 0); b.scale(-1, 1); b.drawImage(img, 0, gy - 14, 16, 14); }
          else b.drawImage(img, sx, gy - 14, 16, 14);
          b.restore();
        } else {
          if (en.dead > 0) { b.drawImage(this.art.ball, sx, gy - 6, 14, 6); continue; }
          const ey = Math.round(gy - en.y - 14);
          b.fillStyle = 'rgba(0,0,0,0.3)'; b.fillRect(sx + 2, gy - 1, 10, 1);
          b.drawImage(this.art.ball, sx, ey, 14, 14);
        }
      }

      // Player (and whatever they're chewing)
      this.drawPlayer(cam);
      if (!(this.invuln > 0 && Math.floor(this.time * 16) % 2)) this.drawEat(cam);

      // Particles
      for (const q of this.particles) {
        const sx = Math.round(q.x - cam), sy = Math.round(gy - q.y);
        if (q.text) drawText(b, q.text, sx - 3, sy, this.accent, 1, '#14141a');
        else { b.fillStyle = q.color; b.fillRect(sx, sy, 1, 1); }
      }

      this.drawWarnings(cam);

      if (this.flash > 0) {
        b.fillStyle = `rgba(255,255,255,${this.flash * 0.5})`;
        b.fillRect(0, 0, iw, ih);
      }

      // Scale up without smoothing
      const c = this.ctx;
      c.imageSmoothingEnabled = false;
      c.drawImage(this.buf, 0, 0, iw * RES, ih * RES, 0, 0, iw * this.sc, ih * this.sc);
    }

    // Painted backdrop: sky with dithered gradient, sun/moon, streaky clouds, then the New York scenery.
    paintBackground(cam, th) {
      const b = this.b, iw = this.iw, gy = this.gy, t = this.time;
      const top = -60, H = gy + 1 - top, bands = 56;
      const stops = [th.sky0, th.sky1, th.sky2, th.sky3];
      for (let i = 0; i < bands; i++) {
        const u = i / (bands - 1), seg = u * 3, k = Math.min(2, Math.floor(seg));
        b.fillStyle = mixHex(stops[k], stops[k + 1], seg - k);
        b.fillRect(0, top + (i * H) / bands, iw, H / bands + 0.25);
      }
      if (th.night > 0.02) {
        b.globalAlpha = th.night; b.fillStyle = '#ffffff';
        for (let i = 0; i < 40; i++) {
          const x = (((hash(i) * iw * 3 - cam * 0.03) % iw) + iw) % iw, y = hash(i + 50) * (gy - 50);
          b.fillRect(x, y, 0.5, 0.5);
          if (i % 6 === 0 && Math.sin(t * 3 + i) > 0.6) { b.fillRect(x - 0.5, y, 1.5, 0.25); b.fillRect(x, y - 0.5, 0.25, 1.5); }
        }
        b.globalAlpha = 1;
      }
      if (th.sun > 0.02) {
        // big setting sun with retro gaps in its lower half
        const cx = iw * 0.66, cy = gy - FLOOR_BACK - 46, r = 17;
        b.globalAlpha = th.sun;
        for (let dy = -r; dy <= r; dy += 0.5) {
          if (dy > 2 && Math.floor((dy - 2) / 2.5) % 2 === 1) continue;
          const w = Math.sqrt(r * r - dy * dy);
          b.fillStyle = mixHex(th.sunCol, th.sky3, Math.max(0, dy / r) * 0.6);
          b.fillRect(cx - w, cy + dy, w * 2, 0.5);
        }
        b.globalAlpha = 1;
      }
      if (th.moon > 0.02) {
        const mx = iw - 28, my = 24, r = 9;
        b.globalAlpha = th.moon;
        for (let dy = -r; dy <= r; dy += 0.25) for (let dx = -r; dx <= r; dx += 0.25) {
          const d = dx * dx + dy * dy, bite = (dx - 4) * (dx - 4) + (dy + 3) * (dy + 3);
          if (d <= r * r && bite > (r - 1) * (r - 1)) { b.fillStyle = d > (r - 1) * (r - 1) ? '#8fd61e' : this.accent; b.fillRect(mx + dx, my + dy, 0.25, 0.25); }
        }
        b.globalAlpha = 1;
      }
      if (th.glow > 0.02) {
        for (let i = 0; i < 14; i++) { b.fillStyle = `rgba(170,255,40,${(0.06 * th.glow * (14 - i)) / 14})`; b.fillRect(0, gy - 70 + i * 5, iw, 5); }
        if (Math.sin(t * 1.7) > 0.97) { b.fillStyle = 'rgba(220,255,160,0.18)'; b.fillRect(0, top, iw, H); }   // distant lightning
      }
      // streaky painted clouds
      if (th.clouds > 0.02) {
        b.globalAlpha = th.clouds;
        const base = cam * 0.06 + t * 1.5;
        for (let k = Math.floor(base / 70) - 2; k < Math.floor(base / 70) + iw / 70 + 2; k++) {
          const w = 34 + hash(k) * 70, y = 6 + hash(k + 9) * (gy - 70), x = k * 70 + hash(k + 3) * 30 - base;
          for (let r = 0; r < 4; r++) {
            const rw = w * (1 - r * 0.18), rx = x + (w - rw) * (0.3 + hash(k + r) * 0.4), ry = y - r * 1.5;
            b.fillStyle = r === 0 ? th.cloudDk : r === 3 ? th.cloudHi : th.cloud;
            b.fillRect(rx, ry, rw, 1.75);
            b.fillStyle = th.cloudHi; b.fillRect(rx + rw * 0.15, ry, rw * 0.55, 0.25);
          }
        }
        b.globalAlpha = 1;
      }
      // New York backdrop: skyline, buildings or trees, street or park edge, street furniture
      if (this.scenery) this.scenery.draw(b, this.themeFrom, this.themeTo, this.themeT, cam, gy - FLOOR_BACK, iw, t);
    }

    // Floor in perspective (sidewalk slabs in Manhattan, a speckled path in the park): each row scrolls a
    // little faster toward the viewer, so the seams lean as the runner moves. Pits cut through it.
    paintFloor(cam, th) {
      const b = this.b, bw = this.buf.width, y0 = Math.round((this.gy - FLOOR_BACK) * RES), rows = this.buf.height - y0;
      if (rows <= 0) return;
      if (!this.floorImg || this.floorImg.width !== bw || this.floorImg.height !== rows) this.floorImg = b.createImageData(bw, rows);
      const img = this.floorImg, d = img.data;
      const ramp = th.floor.map(hexRgb);
      // which screen columns are over a pit
      const open = new Uint8Array(bw);
      for (let x = 0; x < bw; x++) open[x] = 1;
      for (const sp of this.level.ground) {
        const a = Math.max(0, Math.floor((sp[0] - cam) * RES)), e = Math.min(bw, Math.ceil((sp[1] - cam) * RES));
        for (let x = a; x < e; x++) open[x] = 0;
      }
      const slab = th.slab > 0.5, PL = slab ? 22 : 14, cx = this.iw / 2;
      for (let j = 0; j < rows; j++) {
        // nearer rows show the slabs bigger, so seams fan out from a vanishing point and slide faster
        const depth = j / rows, f = 1 + depth * 0.9;
        const rowSeam = slab && (Math.abs(depth - 0.3) < 0.6 / rows || Math.abs(depth - 0.66) < 0.8 / rows);
        const band = depth < 0.3 ? 0 : depth < 0.66 ? 1 : 2;
        for (let x = 0; x < bw; x++) {
          const i = (j * bw + x) * 4;
          let c;
          if (open[x]) {
            const v = 18 - depth * 10;
            d[i] = v + 6; d[i + 1] = v; d[i + 2] = v - 4; d[i + 3] = 255;
            continue;
          }
          const u = cam + (x / RES - cx) / f, plank = Math.floor(u / PL), fu = u - plank * PL;
          if (j < 2) c = ramp[5];                                   // shadow line at the back edge
          else if (j < 4) c = slab ? ramp[0] : ramp[4];             // curb top / path edge
          else if (slab && (fu < 0.5 / f || rowSeam)) c = ramp[4];  // slab joints
          else {
            const n = hash(Math.floor(u * RES) * 7 + j * 131);
            let k = slab ? 1 + ((hash(plank * 13 + band * 7) * 2.4) | 0) : 2;
            if (n > 0.965) k = 4; else if (n > 0.9) k = Math.min(4, k + 1); else if (n < 0.05) k = 0; else if (n < 0.14) k = Math.max(0, k - 1);
            if (slab && fu > PL - 0.8 / f) k = 0;                   // lit slab edge
            c = ramp[k];
          }
          d[i] = c[0]; d[i + 1] = c[1]; d[i + 2] = c[2]; d[i + 3] = 255;
        }
      }
      b.putImageData(img, 0, y0);
    }

    // A blinking "!" at the right edge for the next hazard just outside the view
    drawWarnings(cam) {
      if (this.state !== 'run' || this.attract || Math.floor(this.time * 6) % 2) return;
      const b = this.b, L = this.level, right = cam + this.iw, gy = this.gy;
      let best = null;
      const consider = (x, h) => { if (x > right && x < right + 70 && (!best || x < best.x)) best = { x, h }; };
      for (let k = 0; k < L.ground.length - 1; k++) consider(L.ground[k][1], 0);
      for (const bl of L.blocks) consider(bl.x, bl.h);
      for (const en of L.enemies) if (!en.dead) consider(en.x, en.y + 7);
      if (!best) return;
      const x = this.iw - 7, y = Math.max(14, gy - best.h - 12);
      b.fillStyle = '#000000'; b.fillRect(x - 0.5, y - 0.5, 6, 8);
      b.fillStyle = '#ff3b30'; b.fillRect(x, y, 5, 7);
      drawText(b, '!', x + 1, y + 1, '#ffffff');
    }

    drawPlayer(cam) {
      const b = this.b, p = this.p, gy = this.gy;
      if (this.state === 'crash' && this.fell) return;
      const outfit = this.frameOutfit || OUTFITS[this.save.outfit] || OUTFITS[0];
      const fr = runnerFrames(outfit);
      if (!fr) return;
      const R = ART.runner, W = R.w / RES, H = R.h / RES;  // 200×152 frame = 50×38 world pixels
      const sx = Math.round(p.x - cam) + 6 - R.hipX / RES, sy = Math.round(gy - p.y) - R.feet / RES;
      if (outfit.aura && this.state !== 'crash') this.drawAura(sx + R.hipX / RES - 11, sy + R.feet / RES - 34, 22, 34); // around the body, not the whole frame
      if (this.invuln > 0 && Math.floor(this.time * 16) % 2) return;
      // flying kick for the double jump and for stomping an enemy, like a fighting-game special move
      const punching = fr.punch && (p.spin > 0 || this.punchT > 0);
      const img = this.state === 'crash' ? fr.jump : punching ? fr.punch : !p.grounded ? fr.jump : fr.run[Math.floor(p.x / Math.max(4, 24 / fr.run.length)) % fr.run.length]; // a new frame every 4 world px at most
      if (this.state === 'crash') {
        b.save(); b.translate(sx, sy + H); b.scale(1, -1); b.drawImage(img, 0, 0, W, H); b.restore();
        return;
      }
      if (p.y >= 0 && this.onGround(p.x, p.x + PW)) {
        // soft oval shadow on the floor, smaller the higher the jump
        const k = Math.max(0.35, 1 - p.y / 70), cx = sx + R.hipX / RES, rw = 8 * k;
        b.fillStyle = 'rgba(40,10,0,0.35)';
        for (let dy = -1; dy <= 1; dy += 0.25) { const w = rw * Math.sqrt(1 - dy * dy); b.fillRect(cx - w, gy + 1 + dy, w * 2, 0.25); }
      }
      if (p.spin > 0 && !fr.punch) {
        // Double jump: one forward flip around the sprite's center
        b.save();
        b.translate(sx + W / 2, sy + H / 2);
        b.rotate((1 - p.spin / PHY.SPIN) * Math.PI * 2);
        b.drawImage(img, -W / 2, -H / 2, W, H);
        b.restore();
        return;
      }
      b.drawImage(img, sx, sy, W, H);
    }

    // Gigachad: a body-shaped glow plus flickering neon flames and rising sparks
    drawAura(sx, sy, W, H) {
      const b = this.b, t = this.time, cx = sx + W / 2, cy = sy + H / 2 + 1;
      for (let dy = -H / 2 - 4; dy <= H / 2 + 1; dy += 0.5) {
        const half = (W / 2 + 3) * Math.sqrt(Math.max(0, 1 - (dy / (H / 2 + 4)) ** 2));
        b.fillStyle = 'rgba(150,255,30,0.13)';
        b.fillRect(cx - half, cy + dy, half * 2, 0.5);
      }
      for (let i = 0; i <= 16; i++) {
        const u = i / 16, fx = sx - 3 + u * (W + 6);
        const h = H * (0.45 + 0.6 * Math.sin(Math.PI * u)) * (0.8 + 0.2 * Math.sin(t * 15 + i * 2.3));
        const top = sy + H - h;
        b.fillStyle = 'rgba(140,255,20,0.32)'; b.fillRect(fx, top, 1, h);
        b.fillStyle = 'rgba(214,255,120,0.55)'; b.fillRect(fx, top, 1, 1.5);
      }
      b.fillStyle = '#e4ff8a';
      for (let k = 0; k < 6; k++) {
        const ph = (t * 1.6 + k * 0.17) % 1, seed = Math.floor(t * 1.6 + k * 0.17) * 7 + k;
        b.fillRect(sx - 2 + hash(seed) * (W + 4), sy + H - ph * (H + 10), 0.5, 1);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Mounting
  // ---------------------------------------------------------------------------
  function mount(scope) {
    (scope || document).querySelectorAll('[data-jawline-run-fighter]').forEach((el) => {
      if (!el.__jlrFighter) el.__jlrFighter = new Game(el);
    });
  }
  function unmount(scope) {
    (scope || document).querySelectorAll('[data-jawline-run-fighter]').forEach((el) => el.__jlrFighter && el.__jlrFighter.destroy());
  }

  // sprites: lets a landing page reuse the pixel art (runner outfits, enemies, flavor pouches)
  window.JawlineRunFighter = { mount, unmount, STAGES, THEMES, PHY, OUTFITS, FLAVORS, cleanName, ART, drawText, sprites: { runnerFrames, buildArt }, _buildLevel: buildLevel };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  // Shopify theme editor: section reloaded / removed
  document.addEventListener('shopify:section:load', (e) => mount(e.target));
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));
})();
